/**
 * Makes a full-screen mobile overlay (bottom sheet, drawer, full-screen panel)
 * dismissable by the platform back gesture/button — the Android convention for
 * modal surfaces. While at least one overlay is open, a single sentinel history
 * entry sits on the stack: Back pops the sentinel (URL unchanged, so the router
 * does not navigate) and the TOP-MOST overlay closes; a UI-initiated close
 * (X, backdrop, Escape) of the last open overlay consumes the sentinel with
 * `history.back()` so the NEXT Back press behaves normally.
 *
 * All open overlays share ONE sentinel through a module-level registry instead
 * of pushing one entry each. That registry is what makes the hook safe against
 * the three races a naive per-mount push/back pair loses:
 *
 * - **StrictMode remount** (dev): mount → cleanup → remount used to run
 *   `pushState, back(), pushState` back-to-back; the queued traversal then
 *   popped the remount's fresh sentinel and instantly self-closed every
 *   overlay that mounts while open (PanelModal, the conversation sheet).
 *   Now the cleanup only *schedules* consumption; the remount's register
 *   cancels it and adopts the still-present sentinel.
 * - **Close A, open B in one commit** (e.g. a More-sheet row opening a panel):
 *   A's consume no longer fires between A's cleanup and B's effect — B adopts
 *   A's sentinel.
 * - **Stacked overlays**: one Back closes only the top overlay; the sentinel is
 *   re-armed for the ones below instead of every layer closing at once.
 *
 * @module
 */

import { useEffect, useRef } from 'react'

/** Marker stamped on the sentinel entry so we never pop someone else's state. */
const SENTINEL = 'molOverlay'

interface OverlayEntry {
  onClose: () => void
  /** Set when a Back-gesture pop closed this overlay — its unregister must not consume. */
  closedByPop: boolean
}

/** Open overlays, bottom → top. */
const stack: OverlayEntry[] = []
/** Deferred sentinel consumption (lets a remount / successor adopt it instead). */
let pendingConsume: ReturnType<typeof setTimeout> | null = null
/** True between our own consume `back()` and its popstate echo. */
let consumeInFlight = false
let listenerBound = false

function stateHasSentinel(): boolean {
  return Boolean((window.history.state as Record<string, unknown> | null)?.[SENTINEL])
}

function pushSentinel(): void {
  window.history.pushState({ [SENTINEL]: true }, '')
}

function handlePop(): void {
  if (consumeInFlight) {
    consumeInFlight = false
    // Echo of our own consume traversal. If an overlay opened while it was in
    // flight, the traversal just ate its sentinel — re-arm instead of closing.
    if (stack.length > 0 && !stateHasSentinel()) pushSentinel()
    return
  }
  if (stack.length === 0) return
  // A real Back gesture/button: close the top-most overlay only.
  const top = stack[stack.length - 1]
  top.closedByPop = true
  top.onClose()
  // The popped traversal consumed the sentinel; overlays underneath still need
  // one so the NEXT Back closes them rather than navigating the route. (`top`
  // is still registered here — its effect cleanup runs after React processes
  // the close — hence `> 1`.)
  if (stack.length > 1 && !stateHasSentinel()) pushSentinel()
}

function register(entry: OverlayEntry): void {
  if (!listenerBound) {
    listenerBound = true
    window.addEventListener('popstate', handlePop)
  }
  if (pendingConsume !== null) {
    // Adopt the sentinel the just-unregistered overlay was about to consume.
    clearTimeout(pendingConsume)
    pendingConsume = null
  } else if (stack.length === 0 && !stateHasSentinel()) {
    pushSentinel()
  }
  stack.push(entry)
}

function unregister(entry: OverlayEntry): void {
  const i = stack.indexOf(entry)
  if (i >= 0) stack.splice(i, 1)
  if (stack.length === 0 && !entry.closedByPop) {
    // UI-initiated close of the last overlay: the sentinel is still on the
    // stack. Consume it on a macrotask, NOT synchronously — a StrictMode
    // remount or a successor overlay registering in this same commit cancels
    // this and keeps the sentinel armed.
    pendingConsume = setTimeout(() => {
      pendingConsume = null
      if (stack.length === 0 && stateHasSentinel()) {
        consumeInFlight = true
        window.history.back()
      }
    }, 0)
  }
}

/**
 * Binds the overlay's lifetime to the shared history sentinel while
 * `open && enabled`.
 * @param open - Whether the overlay is currently open.
 * @param onClose - Called when the platform back gesture pops the sentinel.
 * @param enabled - Gate (e.g. `isMobile`) — desktop dialogs must not touch the
 * history stack. Defaults to true.
 */
export function useHistoryDismissable(open: boolean, onClose: () => void, enabled = true): void {
  // Ref so the effect doesn't re-run (re-registering) when the caller passes a
  // new onClose identity each render.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open || !enabled || typeof window === 'undefined') return
    const entry: OverlayEntry = { onClose: () => onCloseRef.current(), closedByPop: false }
    register(entry)
    return () => unregister(entry)
  }, [open, enabled])
}
